import { useState } from 'react';

import { useMutation } from '@apollo/client';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import type { CuiOptionType } from '@crehana/ui';

import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';
import useReportsAmplitude from '@/views/AdminDashboard/hooks/useReportsAmplitude';
import useTranslateCuiOptions from '@/views/AdminDashboard/hooks/useTranslateCuiOptions';
import { USER_STATES } from '@/views/AdminDashboard/pages/Reports/constants';

import type { SendNamedReportProps } from '../../types';
import { AMPLITUDE } from './amplitude';
import SEND_ORGANIZATION_REPORT from './graphql/SendOrganizationReport.v4.b2b.graphql';
import type {
  SendOrganizationReport,
  SendOrganizationReportVariables,
} from './graphql/types/SendOrganizationReport';
import { mapUserStatus } from './utils';

interface DateRangeProps {
  startDate?: string | null;
  endDate?: string | null;
}

const useSendGeneralReport = (
  slug: string,
  availableUserStates?: CuiOptionType<string>[],
) => {
  const { t } = useTranslation();
  const defaultUserStates = useTranslateCuiOptions(
    USER_STATES,
  ) as CuiOptionType<string>[];
  const { logSubmit } = useReportsAmplitude(slug);
  const { openSnackbar } = useAdminDashboard();
  const [variables, setVariables] = useState<SendOrganizationReportVariables>({
    slug,
    startDate: null,
    endDate: null,
    userStatus: mapUserStatus(availableUserStates || defaultUserStates),
  });

  const [sendOrganizationReport, { loading, error }] = useMutation<
    SendOrganizationReport,
    SendOrganizationReportVariables
  >(SEND_ORGANIZATION_REPORT, {
    variables,
    context: { clientName: 'v4.b2b' },
    onCompleted({ reportOrganizationProgressCall }) {
      logSubmit({
        event: AMPLITUDE.GENERAL_REPORT_SEND,
        reportType: AMPLITUDE.GENERAL_REPORT,
        filters: variables,
        status: reportOrganizationProgressCall?.success
          ? AMPLITUDE.GENERAL_REPORT_SUCCESS
          : AMPLITUDE.GENERAL_REPORT_FAILED,
      });
      if (!reportOrganizationProgressCall?.success) {
        openSnackbar({
          type: 'alert',
          msg: t('SNACKBAR_DEFAULT_ERROR'),
        });
      }
    },
    onError() {
      openSnackbar({
        type: 'alert',
        msg: t('SNACKBAR_DEFAULT_ERROR'),
      });
    },
  });

  const setReportDates = (variables: DateRangeProps) => {
    setVariables(prevState => ({ ...prevState, ...variables }));
  };

  const setReportUserStatus = (selected: CuiOptionType[]) => {
    if (selected) {
      setVariables(prevState => ({
        ...prevState,
        userStatus: mapUserStatus(
          selected.length === 0 ? defaultUserStates : selected,
        ),
      }));
    }
  };

  const handleSendNamedReport = ({
    name = t('REPORT_1_TITLE'),
    description = t('REPORT_1_DESCRIPTION'),
  }: SendNamedReportProps) => {
    return sendOrganizationReport({
      variables: { ...variables, name, description },
    });
  };

  return {
    handleSendNamedReport,
    filters: variables,
    setReportDates,
    setReportUserStatus,
    sendOrganizationReport,
    loading,
    error,
  };
};

export default useSendGeneralReport;
